import React, { useState, useEffect, useRef, useMemo } from "react";
import { useParams, useLocation } from "react-router-dom";
import dayjs from "dayjs";
import { usePost } from "../../hooks/usePost";
import { useNavigate } from "react-router-dom";
import { Tooltip } from "antd";
import styled from "styled-components";
import { useToast } from "../../store/ToastProvider";
import { NavBar } from "../../components/common/NavBar";
import { SideMenu } from "../../components/common/SideMenu";
import { CompanyStatusHeader } from "../CompanyProfile/CompanyStatusheader";
import { greyText } from "../../styles";
import {
  BackgroundContainer,
  Card,
  MainColumn as Column,
  ElementContainer,
  FlexibleContainer,
  NavBarContainer,
  RowContainer,
} from "../../components/containers/styledComponents";
import { getPathwayFromInput } from "../../components/visuals/PathwayProgressBar";
import {
  CompassMainHeader,
  CompassParagraph,
} from "../../components/common/styledComponents";
import { formatReadable, getSdgIcon } from "../../utils/formattingUtils";
import { MESSAGES } from "../../messages";
import {
  CompanySummary,
  CompanySummaryRequest,
} from "../../models/CompanyprofileModels";
import { ROUTES } from "../../routes/routeNames";
import { Icon, IconContainer } from "../../components/visuals/styledComponents";
import companyDescriptionActive from "../../assets/svg/company_description_active.svg";
import medalActive from "../../assets/svg/medal_active.svg";
import editIcon from "../../assets/svg/edit.svg";
import plusIcon from "../../assets/svg/plus.svg";
import downArrowIcon from "../../assets/svg/down_arrow_icon.svg";
import { AssessmentSummaryButton } from "./AssessmentSummaryButton";
import { KeyValueTable } from "../../components/tables/KeyValueTable";
import {
  CustomTag,
  DataEntry,
  EngagementCategory,
  EngagementRecord,
  EngagementType,
  StewardshipFilterParam,
} from "../../models/stewardshipModels";
import { CompassTag } from "../../components/visuals/CompassTag";
import { InlineLoadingPlaceholder } from "../../components/visuals/InlineLoadingPlaceholder";
import { MilestoneItem } from "../CompanyEngagements/shared/MilestoneCard";
import { MilestoneKey } from "../../enums/Status";
import { EscalationParentType } from "../../enums/Escalations";
import {
  buildEngagementOverview,
  buildEscalations,
  buildMilestoneItem,
} from "../CompanyEngagements/utils";
import { ESG_ENGAGEMENT_THEMES } from "../CompanyEngagements/constants";
import { CompassModal, ModalFooter } from "../../components/common/Modal";
import {
  EngagementRecordForm,
  EngagementRecordFormRef,
  FormMode,
} from "../StewardshipHomepage/EngagementRecordForm";
import { EngagementTimeline } from "../CompanyEngagements/components/EngagementTimeline";
import { PageButton } from "../../components/visuals/PageButton";
import {
  EngagementUpdateForm,
  EngagementUpdateFormRef,
} from "./EngagementUpdateForm";
import { resolveEngagementMilestone } from "../../utils/engagementMilestones";
import { EscalationRail, MilestoneRail } from "./EngagementRail";

const RAIL_BREAKPOINT = 960;

const PageBody = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 16px;
  width: 100%;
  box-sizing: border-box;
  padding: 4px 30px 30px 20px;

  @media (max-width: ${RAIL_BREAKPOINT}px) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const MainStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  flex: 1;
  min-width: 0;
`;

const Rail = styled.aside`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 320px;
  flex-shrink: 0;

  @media (max-width: ${RAIL_BREAKPOINT}px) {
    width: 100%;
  }
`;

const BackButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 42px;
  height: 42px;
  border: none;
  background: #f0f0f0;
  border-radius: 8px;
  cursor: pointer;
  margin-right: 16px;
  transition:
    transform 0.15s ease-in-out,
    background 0.15s ease-in-out;

  &:hover {
    transform: scale(1.08);
    background: #e0e0e0;
  }

  img {
    transform: rotate(90deg);
    width: 20px;
    height: 20px;
  }
`;

export const SingleEngagement = (): JSX.Element => {
  const [show, setShow] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showAddUpdateModal, setShowAddUpdateModal] = useState<boolean>(false);
  const [isFormLoading, setIsFormLoading] = useState<boolean>(false);
  const [isUpdateFormLoading, setIsUpdateFormLoading] =
    useState<boolean>(false);
  const [timelineRefreshKey, setTimelineRefreshKey] = useState<number>(0);
  const formRef = useRef<EngagementRecordFormRef>(null);
  const updateFormRef = useRef<EngagementUpdateFormRef>(null);
  const [companyName, setCompanyName] = useState<string>("");
  const [engagement, setEngagement] = useState<EngagementRecord | null>(null);
  const { engagementId } = useParams();
  const location = useLocation();
  const { addToast } = useToast();
  const navigate = useNavigate();

  // Get engagement record from navigation state if available
  const stateEngagementRecord = (
    location.state as { engagementRecord?: EngagementRecord } | null
  )?.engagementRecord;

  const {
    data: companySummaryData,
    sendData: getCompanySummary,
    isLoading: isCompanyLoading,
    error: summaryDataError,
  } = usePost<CompanySummary, CompanySummaryRequest>();

  const {
    data: engagementData,
    sendData: getEngagementData,
    isLoading: isEngagementDataLoading,
  } = usePost<EngagementRecord[], StewardshipFilterParam>();

  const {
    data: deleteUpdateData,
    sendData: deleteEngagementUpdate,
    error: deleteUpdateError,
    isLoading: isDeleting,
  } = usePost<{ message: string }, { update_id: string }>();

  // Fetch custom tags to map tag IDs to display names/colors
  const { data: customTags, sendData: fetchCustomTags } = usePost<
    CustomTag[],
    null
  >();

  // Fetch custom tags on mount
  useEffect(() => {
    fetchCustomTags("GET_CUSTOM_TAGS", null);
  }, [fetchCustomTags]);

  // Map tag IDs to CustomTag objects for display
  const engagementTags = useMemo(() => {
    if (
      !engagement?.tags ||
      !Array.isArray(engagement.tags) ||
      !customTags?.length
    )
      return [];
    const tagMap = new Map(customTags.map((t) => [t.id, t]));
    return engagement.tags
      .map((tagId) => tagMap.get(tagId))
      .filter((tag): tag is CustomTag => !!tag);
  }, [engagement?.tags, customTags]);

  const handleDeleteUpdate = (updateId: string): void => {
    if (window.confirm("Are you sure you want to delete this update?")) {
      deleteEngagementUpdate("DELETE_ENGAGEMENT_UPDATE", {
        update_id: updateId,
      });
    }
  };

  // Show toast on delete error
  useEffect(() => {
    if (deleteUpdateError) {
      addToast("Failed to delete update", "error");
    }
  }, [deleteUpdateError]);

  // Refresh timeline after successful delete
  useEffect(() => {
    if (deleteUpdateData) {
      addToast("Update deleted successfully", "success");
      setTimelineRefreshKey((prev) => prev + 1);
    }
  }, [deleteUpdateData]);

  const pathway = getPathwayFromInput(
    companySummaryData?.implicit_temperature || MESSAGES.NOT_AVAILABLE,
  );

  const engagementProperties: DataEntry[] = engagement
    ? buildEngagementOverview(engagement)
    : [];

  const SdgsImgs = (): JSX.Element => {
    const engagementSgdNums = engagement
      ? ESG_ENGAGEMENT_THEMES[engagement.meta_theme as EngagementType] || []
      : [];
    return (
      <FlexibleContainer jc="flex-start" ml="-8px">
        {engagementSgdNums.length ? (
          engagementSgdNums.map((sdg, index) => (
            <IconContainer key={index} p={1} m={1} size={45}>
              <Icon src={getSdgIcon(sdg)} />
            </IconContainer>
          ))
        ) : (
          <CompassParagraph>{MESSAGES.NOT_AVAILABLE}</CompassParagraph>
        )}
      </FlexibleContainer>
    );
  };
  const engagementSdgs: DataEntry = { label: "SDG", value: <SdgsImgs /> };

  const milestoneItems: MilestoneItem[] = engagement
    ? Object.keys(MilestoneKey)
        .slice(1, 5)
        .map((key) => buildMilestoneItem(engagement, key as MilestoneKey))
    : [];

  const escalations: EscalationParentType[] = engagement
    ? buildEscalations(engagement)
    : [];

  // Use engagement from navigation state if available, otherwise fetch
  useEffect(() => {
    if (stateEngagementRecord) {
      // Use data from navigation state - no API call needed
      setEngagement(stateEngagementRecord);

      // Fetch company summary using the company ID from the engagement
      if (stateEngagementRecord.company) {
        getCompanySummary("GET_COMPANY_SUMMARY", {
          compass_cid: stateEngagementRecord.company,
          company_name: "",
        });
      }
    } else if (engagementId) {
      // No state data - need to fetch from API
      getEngagementData("GET_ENGAGEMENT_DATA", {
        engagement_record_id: [engagementId],
      });
    }
  }, [engagementId, stateEngagementRecord]);

  // When engagement data loads from API, set the engagement and fetch company summary
  useEffect(() => {
    // Only process API data if we don't have state data
    if (!stateEngagementRecord && engagementData?.length && engagementId) {
      // Find the engagement that matches the ID from the URL
      const engagementRecord = engagementData.find(
        (e) => e.engagement_record_id === engagementId,
      );

      if (engagementRecord) {
        setEngagement(engagementRecord);

        // Fetch company summary using the company ID from the engagement
        if (engagementRecord.company) {
          getCompanySummary("GET_COMPANY_SUMMARY", {
            compass_cid: engagementRecord.company,
            company_name: "",
          });
        }
      }
    }
  }, [engagementData, engagementId, stateEngagementRecord]);

  useEffect(() => {
    if (companySummaryData) {
      setCompanyName(formatReadable(companySummaryData.name));
    }
  }, [companySummaryData]);

  useEffect(() => {
    if (summaryDataError) {
      addToast(MESSAGES.SCREENING_TOOL.ERROR_FETCHING_COMPANY_SUMMARY, "error");
      navigate(ROUTES.SCREENING_TOOL);
    }
  }, [summaryDataError]);

  return (
    <BackgroundContainer>
      <Column flex={show ? "0 0 15%" : "0 0 0%"} show={show}>
        <SideMenu show={show} setShow={setShow} />
      </Column>
      <Column flex="1" bg="#F5F5F5" overflow={true}>
        <NavBarContainer>
          <RowContainer mt={1} mb={1}>
            <NavBar
              address={
                <>
                  company-engagements /{" "}
                  {companyName ? (
                    companyName
                  ) : (
                    <InlineLoadingPlaceholder label="Loading company name" />
                  )}{" "}
                  /{" "}
                  {engagement?.engagement_name ? (
                    engagement.engagement_name
                  ) : (
                    <InlineLoadingPlaceholder
                      label="Loading engagement name"
                      width="190px"
                    />
                  )}
                </>
              }
            />
          </RowContainer>
          <RowContainer ai={"center"} jc={"flex-start"} mt={-15}>
            <CompassMainHeader pt={1} ml={12}>
              {engagement?.engagement_name || "Engagement Details"}
            </CompassMainHeader>
            {/* Custom Tags Display */}
            {engagementTags.length > 0 && (
              <FlexibleContainer
                jc="flex-start"
                ai="center"
                ml={"16px"}
                style={{ gap: "8px" }}
              >
                {engagementTags.map((tag) => (
                  <CompassTag
                    key={tag.id}
                    tagKey={tag.id}
                    displayValue={tag.display_name}
                    bg={tag.color}
                    color="white"
                  />
                ))}
              </FlexibleContainer>
            )}
          </RowContainer>
          <RowContainer ai={"flex-start"} jc={"flex-start"} mt={1}>
            <CompassParagraph
              ml={20}
              color={greyText}
              style={{ maxWidth: "800px" }}
            >
              {engagement?.engagement_obj_desc || ""}
            </CompassParagraph>
          </RowContainer>
          <RowContainer ml={20} mt={1} ai="flex-end" jc="space-between">
            <FlexibleContainer ai="center">
              <Tooltip title="View all company engagements" placement="bottom">
                <BackButton
                  onClick={(): void => {
                    if (engagement?.company) {
                      navigate(
                        `${ROUTES.COMPANY_ENGAGEMENTS}/${engagement.company}`,
                      );
                    }
                  }}
                  disabled={!engagement?.company}
                >
                  <img src={downArrowIcon} alt="Back" />
                </BackButton>
              </Tooltip>
              <CompanyStatusHeader
                companyId={engagement?.company}
                companyName={companyName}
                pathway={pathway}
                data={companySummaryData}
                isLoading={isCompanyLoading}
              />
            </FlexibleContainer>
            {/* Action Buttons */}
            <ElementContainer mr={40}>
              <FlexibleContainer>
                <PageButton
                  content={
                    <CompassParagraph fontSize={14} cursor="pointer">
                      Edit Engagement
                    </CompassParagraph>
                  }
                  image={editIcon}
                  action={(): void => setShowEditModal(true)}
                  isLoading={isEngagementDataLoading}
                  he={true}
                />
              </FlexibleContainer>
              <FlexibleContainer ml={"8px"}>
                <PageButton
                  content={
                    <CompassParagraph fontSize={14} cursor="pointer">
                      Add Interaction
                    </CompassParagraph>
                  }
                  image={plusIcon}
                  action={(): void => setShowAddUpdateModal(true)}
                  isLoading={isEngagementDataLoading}
                  he={true}
                />
              </FlexibleContainer>
              <FlexibleContainer ml={"8px"} ai="center">
                <AssessmentSummaryButton
                  engagementId={engagementId}
                  onOpen={(): void => {
                    if (engagementId) {
                      navigate(
                        `${ROUTES.ASSESSMENT_VIEW}/${engagementId}`,
                        { state: { engagementRecord: engagement } },
                      );
                    }
                  }}
                />
              </FlexibleContainer>
            </ElementContainer>
          </RowContainer>
        </NavBarContainer>

        <PageBody>
          <MainStack>
            <Card isLoading={isEngagementDataLoading} w={100} h="auto" m="0">
              <EngagementTimeline
                engagementRecordId={engagementId || null}
                engagementCategory={engagement?.engagement_type}
                refreshKey={timelineRefreshKey}
                onDeleteUpdate={handleDeleteUpdate}
                isExternalLoading={isDeleting}
                managerName={engagement?.manager_name}
                managerImage={engagement?.image}
              />
            </Card>

            <RowContainer mt={0} mb={0} ai="stretch" style={{ gap: "16px" }}>
              <Card
                isLoading={isEngagementDataLoading}
                w={40}
                h="auto"
                m="0"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <RowContainer
                  mt={-10}
                  ml={-10}
                  jc="flex-start"
                  ai="flex-start"
                  style={{ flexShrink: 0 }}
                >
                  <ElementContainer ai="center" jc="flex-start">
                    <IconContainer>
                      <Icon
                        src={companyDescriptionActive}
                        alt="Engagement overview icon"
                      />
                    </IconContainer>
                    <CompassParagraph fontSize={16} bold={true}>
                      Engagement Overview
                    </CompassParagraph>
                  </ElementContainer>
                </RowContainer>
                <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                  <KeyValueTable
                    columns={[{ title: "Title" }, { title: "Name" }]}
                    data={engagementProperties.slice(0, 6)}
                  />
                </div>
              </Card>
              <Card
                isLoading={isEngagementDataLoading}
                w={60}
                h="auto"
                m="0"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  overflow: "hidden",
                }}
              >
                <RowContainer
                  mt={-10}
                  ml={-10}
                  jc="flex-start"
                  ai="flex-start"
                  style={{ flexShrink: 0 }}
                >
                  <ElementContainer ai="center" jc="flex-start">
                    <IconContainer>
                      <Icon src={medalActive} alt="Engagement overview icon" />
                    </IconContainer>
                    <CompassParagraph fontSize={16} bold={true}>
                      Engagement Objective
                    </CompassParagraph>
                  </ElementContainer>
                </RowContainer>
                <div style={{ flex: 1, overflowY: "auto", minHeight: 0 }}>
                  <RowContainer>
                    <CompassParagraph color={greyText}>
                      {engagement?.engagement_obj_desc ||
                        MESSAGES.NOT_AVAILABLE}
                    </CompassParagraph>
                  </RowContainer>
                  <KeyValueTable
                    columns={[{ title: "Title" }, { title: "Name" }]}
                    data={[
                      ...engagementProperties.slice(6, 9),
                      engagementSdgs,
                    ]}
                  />
                </div>
              </Card>
            </RowContainer>
          </MainStack>

          <Rail>
            {engagement?.engagement_type === EngagementCategory.Objective && (
              <MilestoneRail
                milestones={milestoneItems}
                isLoading={isEngagementDataLoading}
              />
            )}
            <EscalationRail
              escalations={escalations}
              isLoading={isEngagementDataLoading}
            />
          </Rail>
        </PageBody>
      </Column>

      {/* Edit Engagement Modal */}
      <CompassModal
        show={showEditModal}
        handleClose={(): void => setShowEditModal(false)}
        size="lg"
        title="Edit Engagement"
        titleIcon={editIcon}
        modalType="lg"
        footer={
          <ModalFooter
            onClose={(): void => setShowEditModal(false)}
            onSubmit={(): void => formRef.current?.submit()}
            submitText="Update Engagement Record"
            isLoading={isFormLoading}
            showDivider={false}
          />
        }
      >
        <EngagementRecordForm
          ref={formRef}
          mode={FormMode.Edit}
          initialData={
            engagement
              ? {
                  engagement_record_id: engagement.engagement_record_id,
                  company: engagement.company,
                  manager: engagement.manager_id,
                  engagementCategory: engagement.engagement_type,
                  category: engagement.meta_theme,
                  theme: engagement.theme,
                  subTheme: engagement.sub_theme,
                  objectiveTitle: engagement.engagement_name,
                  objectiveDescription: engagement.engagement_obj_desc,
                  startDate: engagement.engagement_start
                    ? dayjs(engagement.engagement_start)
                    : undefined,
                  endDate: engagement.proj_obj_completion
                    ? dayjs(engagement.proj_obj_completion)
                    : undefined,
                  milestone: resolveEngagementMilestone(engagement),
                  tags: Array.isArray(engagement.tags)
                    ? engagement.tags.map((id) => ({ id }))
                    : undefined,
                }
              : undefined
          }
          onSuccess={(): void => {
            setShowEditModal(false);
            if (engagementId) {
              getEngagementData("GET_ENGAGEMENT_DATA", {
                engagement_record_id: [engagementId],
              });
            }
          }}
          onLoadingChange={setIsFormLoading}
        />
      </CompassModal>

      {/* Add Engagement Update Modal */}
      <CompassModal
        show={showAddUpdateModal}
        handleClose={(): void => setShowAddUpdateModal(false)}
        size="lg"
        title="Add Engagement Update"
        titleIcon={plusIcon}
        modalType="lg"
        footer={
          <ModalFooter
            onClose={(): void => setShowAddUpdateModal(false)}
            onSubmit={(): void => updateFormRef.current?.submit()}
            submitText="Add Update"
            isLoading={isUpdateFormLoading}
            showDivider={false}
          />
        }
      >
        <EngagementUpdateForm
          ref={updateFormRef}
          engagementRecordId={engagement?.engagement_record_id || ""}
          engagementName={engagement?.engagement_name || ""}
          engagementCategory={
            engagement?.engagement_type as EngagementCategory | undefined
          }
          onSubmitStart={(): void => {
            // Close modal immediately when submit starts
            setShowAddUpdateModal(false);
            // Reset loading state since form will unmount
            setIsUpdateFormLoading(false);
          }}
          onSuccess={(): void => {
            // Refresh timeline after adding update
            setTimelineRefreshKey((prev) => prev + 1);
            // Optionally refresh engagement data after adding update
            if (engagementId) {
              getEngagementData("GET_ENGAGEMENT_DATA", {
                engagement_record_id: [engagementId],
              });
            }
          }}
          onLoadingChange={setIsUpdateFormLoading}
        />
      </CompassModal>
    </BackgroundContainer>
  );
};
